import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Link,
  Text,
  Input,
  Button,
  VStack,
  HStack,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  getProductLinks,
  companyLinks,
  resourceLinks,
  footerBottomLinks,
} from "../../constants/navigationData";

const Footer = () => {
  const [productLinks, setProductLinks] = useState([]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const products = await getProductLinks();
        setProductLinks(products);
      } catch (error) {
        console.error("Error loading products for footer:", error);
      }
    };

    loadProducts();
  }, []);

  return (
    <Box
      bgGradient="linear(to-r, #02063A, #0A1068)"
      color="white"
      py={{ base: 8, md: 10, lg: 12 }}
      px={{ base: 4, md: 6, lg: 8 }}
    >
      <Box maxW="1200px" mx="auto">
        {/* Main Footer Content */}
        <Flex
          direction={{ base: "column", lg: "row" }}
          justify="space-between"
          align={{ base: "start", lg: "start" }}
          gap={{ base: 8, md: 10, lg: 12 }}
          mb={0}
        >
          {/* Company Description */}
          <Box maxW={{ base: "100%", lg: "500px" }} mb={{ base: 6, lg: 0 }}>
            <Flex align="center" mb={4}>
              <Box>
                <img
                  src="/png/BA_text.png"
                  alt="Company Logo"
                  style={{ height: "58px", width: "151px" }}
                />
              </Box>
            </Flex>
            <Text fontSize="sm" lineHeight="1.6" opacity={0.9}>
              Bangladesh's most trusted e-Government and enterprise technology
              partner. Delivering innovative solutions that drive digital
              transformation and inclusive growth.
            </Text>
            <SimpleGrid
              columns={{ base: 2, sm: 4 }}
              spacing={{ base: 3, sm: 4 }}
              mt={4}
              maxW="360px"
              alignItems="center"
              justifyItems="center"
            >
              {[
                { src: "/png/ios_round.png", alt: "ISO 27001 Certification" },
                { src: "/png/iso.png", alt: "CMMI Level 3 Appraised" },
                { src: "/png/iso2.png", alt: "ISO 9001 Certification" },
                { src: "/png/duns.png", alt: "Microsoft Partner" },
              ].map((img, i) => (
                <Box
                  key={i}
                  as="figure"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  h="100%"
                  maxW={{ base: "50px", sm: "60px", md: "auto" }}
                  maxH={{ base: "40px", sm: "50px", md: "auto" }}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    style={{
                      width: "100%",
                      height: "100%",
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      display: "block",
                      // filter: "brightness(0) invert(1)",
                    }}
                  />
                </Box>
              ))}
            </SimpleGrid>

            {/* Newsletter Subscription */}
          </Box>

          {/* Footer Links Grid */}
          <SimpleGrid
            columns={{ base: 2, sm: 2, md: 2, lg: 2 }}
            spacing={{ base: 6, md: 8, lg: 12 }}
            flex={1}
            maxW={{ base: "100%", lg: "400px" }}
            ml={{ lg: "auto" }}
            w="full"
          >
            {/* Company */}
            <VStack align="start" spacing={3} w="full">
              <Text fontSize="lg" fontWeight="bold" mb={1}>
                Company
              </Text>
              {companyLinks.map((link, i) => (
                <Link
                  key={i}
                  href={link.href}
                  fontSize="sm"
                  opacity={0.8}
                  _hover={{ opacity: 1, textDecoration: "none" }}
                  display="block"
                  w="fit-content"
                >
                  {link.title}
                </Link>
              ))}
            </VStack>

            {/* Resources */}
            <VStack align="start" spacing={3} w="full">
              <Text fontSize="lg" fontWeight="bold" mb={1}>
                Resources
              </Text>
              {resourceLinks.map((link, i) => (
                <Link
                  key={i}
                  href={link.href}
                  fontSize="sm"
                  opacity={0.8}
                  _hover={{ opacity: 1, textDecoration: "none" }}
                  target={link.title === "Support" ? "_blank" : undefined}
                  rel={
                    link.title === "Support" ? "noopener noreferrer" : undefined
                  }
                  display="block"
                  w="fit-content"
                  whiteSpace="nowrap"
                >
                  {link.title}
                </Link>
              ))}
            </VStack>
          </SimpleGrid>
        </Flex>

        {/* Email Subscription and Social Media Row */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          mt={6}
          gap={{ base: 4, md: 8 }}
        >
          {/* Email Subscription */}
          <HStack
            spacing={0}
            maxW="320px"
            w={{ base: "full", md: "auto" }}
            direction={{ base: "column", sm: "row" }}
            as={Flex}
          >
            <Input
              placeholder="Email address"
              bg="white"
              color="black"
              border="none"
              borderRadius={{ base: "25px", sm: "25px 0 0 25px" }}
              px={4}
              py={2}
              fontSize="sm"
              _placeholder={{ color: "gray.500" }}
              mb={{ base: 2, sm: 0 }}
              w="full"
            />
            <Button
              bg="#ff5722"
              color="white"
              borderRadius={{ base: "25px", sm: "0 25px 25px 0" }}
              px={6}
              py={2}
              fontSize="sm"
              _hover={{ bg: "#e64a19" }}
              w={{ base: "full", sm: "auto" }}
              minW="100px"
            >
              Subscribe
            </Button>
          </HStack>

          {/* Social Media Section */}
          <Box textAlign={{ base: "left", md: "right" }}>
            <HStack
              spacing={2}
              justify={{ base: "flex-start", md: "flex-end" }}
              align="center"
            >
              <Text fontSize="sm" fontWeight="medium" opacity={0.9}>
                Our Presence Across Platforms
              </Text>

              <Link
                href="https://www.facebook.com/BusinessAutomationLtd"
                target="_blank"
                rel="noopener noreferrer"
                w="32px"
                h="32px"
                borderRadius="50%"
                bg="#4267B2"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ transform: "scale(1.1)", transition: "all 0.2s" }}
              >
                <Box color="white" fontSize="14px">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </Box>
              </Link>

              <Link
                href="https://bd.linkedin.com/company/business-automation-ltd"
                target="_blank"
                rel="noopener noreferrer"
                w="32px"
                h="32px"
                borderRadius="50%"
                bg="#0077B5"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ transform: "scale(1.1)", transition: "all 0.2s" }}
              >
                <Box color="white" fontSize="14px">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </Box>
              </Link>

              <Link
                href="https://www.youtube.com/@businessautomationltd.1998"
                target="_blank"
                rel="noopener noreferrer"
                w="32px"
                h="32px"
                borderRadius="50%"
                bg="#FF0000"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ transform: "scale(1.1)", transition: "all 0.2s" }}
              >
                <Box color="white" fontSize="14px">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </Box>
              </Link>
            </HStack>
          </Box>
        </Flex>

        {/* Footer Bottom */}
        <Box borderTop="1px solid" borderColor="whiteAlpha.200" pt={4} mt={6}>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align="center"
            gap={4}
            textAlign={{ base: "center", md: "left" }}
          >
            <Text fontSize="sm" opacity={0.8}>
              &copy; 2025 BA Systems Limited. All rights reserved.
            </Text>

            <HStack
              spacing={6}
              justify={{ base: "center", md: "flex-end" }}
              wrap="wrap"
              flexWrap="wrap"
            >
              {footerBottomLinks.map((link, i) => (
                <Link
                  key={i}
                  href={link.href}
                  fontSize="sm"
                  opacity={0.8}
                  _hover={{ opacity: 1, textDecoration: "none" }}
                >
                  {link.title}
                </Link>
              ))}
            </HStack>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
